import React, { useContext, useEffect, useState } from "react";
import "./SignIn.css";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { variableContext } from "../../../context/VariableContex";
import useAuthQuery from "../../../store/auth/useAuthQuery";
import Flag from "../../../assets/Flag.svg";
import white from "../../../assets/white.png";
import master from "../../../assets/master.png";

const modelSchema = Yup.object().shape({
  phoneNumber: Yup.number().required("Phone Number is required"),
});

const SignIn = () => {
  const history = useHistory();
  const { setMobile } = useContext(variableContext);
  const [checking, setChecking] = useState(false);
  const initialValues = { phoneNumber: "" };

  const { mutateAsync: checkMobile } = useAuthQuery.CheckMobile();
  const { mutateAsync: sendOtp } = useAuthQuery.SendOtp();

  const formik = useFormik({
    initialValues,
    validationSchema: modelSchema,
    onSubmit: async (values, { setFieldError, setSubmitting }) => {
      const phone = values.phoneNumber.toString();
      if (phone.length < 10 || phone.length > 10) {
        setFieldError("phoneNumber", "Invalid Phone Number");
        return;
      }
      setChecking(true);
      try {
        setMobile(values.phoneNumber);
        // Smart login: check if mobile is already verified
        const result = await checkMobile({ mobile: values.phoneNumber });

        if (result?.data?.is_verified && result?.data?.token?.access) {
          // Already verified — tokens stored in onSuccess, go straight to home
          history.push("/");
        } else if (result?.data?.exists) {
          // Exists but not verified — send OTP
          await sendOtp({ phoneNumber: values.phoneNumber });
          history.push("/sign-in-otp");
        } else {
          setFieldError("phoneNumber", "Mobile No. doesn't exist. Please sign up.");
        }
      } catch (err) {
        // Fallback: if check-mobile endpoint not available, use original OTP flow
        try {
          await sendOtp({ phoneNumber: values.phoneNumber });
          history.push("/sign-in-otp");
        } catch {
          setFieldError("phoneNumber", "Mobile No. doesn't exist");
        }
      } finally {
        setChecking(false);
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="signin-page-wrapper">
      <div className="signin-blue-header">
        <ArrowBackIcon
          onClick={() => history.push("/")}
          className="signin-back-arrow"
        />
        <div className="signin-logo-container">
          <img src={white} className="signin-logo-img" alt="Master Click Logo" />
        </div>
      </div>

      <div className="signin-white-card">
        <div className="signin-title-section">
          <h1 className="signin-main-title">Sign In</h1>
          <p className="signin-subtitle">
            Discover and connect with the best local businesses by signing in to our app today.
          </p>
        </div>

        <form onSubmit={formik.handleSubmit} noValidate>
          <div className="signin-form-field">
            <label className="signin-field-label">Phone Number</label>
            <div className="signin-phone-input-group">
              <div className="signin-flag-box">
                <img src={Flag} alt="India Flag" className="signin-flag-img" />
                <span className="signin-flag-arrow">▼</span>
              </div>
              <div className="signin-phone-input-divider"></div>
              <input
                className="signin-phone-input-field"
                type="number"
                placeholder="xxxxxxx"
                {...formik.getFieldProps("phoneNumber")}
              />
            </div>
            {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
              <div className="signin-error-message">{formik.errors.phoneNumber}</div>
            ) : null}
          </div>

          <div className="signin-button-container">
            <Button
              type="submit"
              className="signin-verify-btn"
              variant="contained"
              disabled={checking || formik.isSubmitting}
              startIcon={checking ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
            >
              {checking ? "Checking…" : <>VERIFY via WHATSAPP <WhatsAppIcon className="signin-btn-wa-icon" /></>}
            </Button>
          </div>
        </form>

        <div className="signin-instructions-section">
          <p className="instructions-title">
            A verification request has been sent to your WhatsApp. Please open it to proceed.
          </p>
          <a
            href="https://wa.me/917293838383"
            target="_blank"
            rel="noreferrer"
            className="instructions-link"
          >
            Tap to open WhatsApp.
          </a>
          <p className="instructions-malayalam">
            (നിങ്ങളുടെ WhatsApp-ലേക്ക് വെരിഫിക്കേഷൻ സന്ദേശം അയച്ചിട്ടുണ്ട്. WhatsApp തുറന്ന് "Approve & Activate" അമർത്തി അക്കൗണ്ട് ആക്റ്റിവേറ്റ് ചെയ്യുക.)
          </p>

          {/* Dynamic WhatsApp Chat Mockup inside phone outline */}
          <div className="phone-mockup-container">
            <div className="phone-notch"></div>
            <div className="phone-screen">
              {/* WhatsApp Header */}
              <div className="wa-header">
                <span className="wa-header-back">←</span>
                <div className="wa-header-avatar">
                  <img src={master} alt="Bot Avatar" />
                </div>
                <div className="wa-header-info">
                  <span className="wa-header-name">Master Click Bot</span>
                  <span className="wa-header-status">Online</span>
                </div>
                <div className="wa-header-icons">
                  <span className="wa-icon">📹</span>
                  <span className="wa-icon">📞</span>
                  <span className="wa-icon">⋮</span>
                </div>
              </div>

              {/* Chat Body */}
              <div className="wa-chat-body">
                <div className="wa-date-pill">Today</div>
                
                <div className="wa-message-bubble">
                  <p className="wa-message-text">
                    Hello! We received a registration request for phone number{" "}
                    <strong>{formik.values.phoneNumber || "7592008384"}</strong>. Is this you?
                  </p>
                  
                  <a
                    href="https://wa.me/917293838383"
                    target="_blank"
                    rel="noreferrer"
                    className="wa-message-button"
                  >
                    ✓ APPROVE & ACTIVATE
                  </a>
                  
                  <div className="wa-message-footer">
                    <span className="wa-footer-text">
                      Tapping Approve will instantly activate your Master Click profile.
                    </span>
                    <span className="wa-message-time">08:09</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "24px",
            marginBottom: "10px",
          }}
        >
          <Typography style={{ fontWeight: "bold", fontSize: "14px", color: "#475569" }}>
            Don't have an account ?
          </Typography>
          <Button
            sx={{ textTransform: "none", textDecoration: "underline", fontWeight: "bold", color: "#2182b1" }}
            onClick={() => history.push("/sign-up")}
          >
            Sign Up
          </Button>
        </Box>
      </div>
    </div>
  );
};

export default SignIn;
